import { z } from "zod"

const SIZE_UNITS: Partial<Record<string, string>> = {
  string: "characters",
  array: "items",
  set: "items",
  file: "bytes",
}

const FORMAT_MESSAGES: Partial<Record<string, string>> = {
  email: "Must be a valid email address",
  url: "Must be a valid URL",
  uuid: "Must be a valid identifier",
  datetime: "Must be a valid date and time",
  date: "Must be a valid date",
  time: "Must be a valid time",
}

/**
 * Determines whether an issue's input represents a missing/empty value,
 * meaning the underlying problem is really "this field is required"
 * rather than some other validation failure (e.g. wrong format/length).
 */
function isEmptyInput(input: unknown): boolean {
  return (
    input === undefined ||
    input === null ||
    input === "" ||
    (Array.isArray(input) && input.length === 0)
  )
}

/**
 * Default, human-friendly error messages for common Zod validation issues.
 * Ensures required fields always show "This field is required" instead of
 * generic messages like "Invalid input" or "String must contain at least 1
 * character(s)", while still providing sensible fallbacks for other issue
 * types (min/max, invalid format, etc).
 *
 * This map only applies when a schema/check does not already provide an
 * explicit message (e.g. via `.refine(fn, { message })`), since those take
 * priority over the global error map.
 */
export const mimberZodErrorMap: z.core.$ZodErrorMap = (issue) => {
  switch (issue.code) {
    case "invalid_type":
      return isEmptyInput(issue.input)
        ? "This field is required"
        : `Invalid value: expected ${issue.expected}`

    case "invalid_value":
      return isEmptyInput(issue.input)
        ? "This field is required"
        : "Please select a valid option"

    case "too_small": {
      if (issue.minimum === 1 && isEmptyInput(issue.input)) {
        return "This field is required"
      }
      const unit = SIZE_UNITS[issue.origin]
      const comparator = issue.inclusive ? "at least" : "more than"
      return unit
        ? `Must be ${comparator} ${issue.minimum} ${unit}`
        : `Must be ${comparator} ${issue.minimum}`
    }

    case "too_big": {
      const unit = SIZE_UNITS[issue.origin]
      const comparator = issue.inclusive ? "at most" : "less than"
      return unit
        ? `Must be ${comparator} ${issue.maximum} ${unit}`
        : `Must be ${comparator} ${issue.maximum}`
    }

    case "invalid_format":
      return isEmptyInput(issue.input)
        ? "This field is required"
        : (FORMAT_MESSAGES[issue.format] ?? "Invalid format")

    case "not_multiple_of":
      return `Must be a multiple of ${issue.divisor}`

    default:
      return isEmptyInput(issue.input) ? "This field is required" : undefined
  }
}

z.config({ customError: mimberZodErrorMap })
